import { motion } from 'framer-motion';
import { CheckCircle, Clock, DollarSign, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import PageHeader from '@/components/common/PageHeader';
import ConsultationForm from '@/components/common/ConsultationForm';
import { usePageMeta } from '@/hooks/useSEO';

const benefits = [
  '10-year renewable residency',
  'No sponsor required',
  'Work or start business freely',
  'Tax-free income',
  'Family included',
  'World-class infrastructure',
];

const UAEGoldenPage = () => {
  usePageMeta(
    'UAE Golden Visa from India 2026 | 10-Year Residency Dubai/Abu Dhabi — Zafco HR',
    'UAE Golden Visa application support by Zafco HR. 10-year renewable residency, no sponsor required, tax-free income, family inclusion. MEA-approved · 10,000+ placements.'
  );
  const openWhatsApp = () => {
    window.open('https://wa.me/919958848998?text=Hello%20Zafco,%20I%20am%20interested%20in%20UAE%20Golden%20Visa', '_blank');
  };

  return (
    <div data-testid="uae-golden-page">
      <PageHeader 
        title="UAE Golden Visa Job Opportunities 2026"
        subtitle="10-year residency for skilled professionals in Dubai and UAE"
        breadcrumbs={[
          { label: 'Countries', path: '#' },
          { label: 'UAE', path: '#' },
          { label: 'Golden Visa' }
        ]}
        backgroundImage="https://images.unsplash.com/photo-1734456083801-5e1a7d20dde2?w=1920"
      />

      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-zafco-teal font-semibold text-sm tracking-wider uppercase">
                UAE Golden Visa
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-zafco-blue font-montserrat mt-2 mb-6">
                UAE Golden Visa Job Assistance
              </h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                The UAE Golden Visa offers 10-year renewable residency for qualified professionals, 
                investors, and entrepreneurs. It provides unprecedented flexibility without 
                employer sponsorship in one of the world's most dynamic economies.
              </p>

              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="bg-gray-50 rounded-xl p-4">
                  <Clock className="w-6 h-6 text-zafco-teal mb-2" />
                  <p className="text-2xl font-bold text-zafco-blue">10 Years</p>
                  <p className="text-sm text-gray-600">Visa Validity</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-4">
                  <DollarSign className="w-6 h-6 text-zafco-teal mb-2" />
                  <p className="text-2xl font-bold text-zafco-blue">AED 30K</p>
                  <p className="text-sm text-gray-600">Min Salary/Month</p>
                </div>
              </div>

              <Button 
                onClick={openWhatsApp}
                className="bg-gradient-to-r from-zafco-gold to-zafco-orange text-white font-semibold px-8"
              >
                <Phone className="w-5 h-5 mr-2" />
                Check Eligibility
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-8 shadow-xl"
            >
              <h3 className="text-xl font-bold text-zafco-blue font-montserrat mb-6">
                Free UAE Assessment
              </h3>
              <ConsultationForm />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit}
                className="bg-white rounded-xl p-6 shadow-sm"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
              >
                <CheckCircle className="w-8 h-8 text-green-500 mb-3" />
                <p className="text-gray-700 font-medium">{benefit}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-zafco-blue to-zafco-teal">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white font-montserrat mb-6">
            Secure Your UAE Golden Visa Today
          </h2>
          <Button 
            onClick={openWhatsApp}
            size="lg"
            className="bg-gradient-to-r from-zafco-gold to-zafco-orange text-white font-semibold px-10 py-7 text-lg"
          >
            <Phone className="w-6 h-6 mr-3" />
            Get Free Consultation
          </Button>
        </div>
      </section>
    </div>
  );
};

export default UAEGoldenPage;
